import type { CallbackInterface } from "recoil";

import { logAppError } from "Helpers/logger";

import { errorUpdater, pageState } from "State/page";
import type { PageStateType } from "Types/state";

import type { NewRecipient } from "Pages/RecipientsList/model";

import {
  panelAddRecipientGroupsState,
  panelAddRecipientState,
} from "Pages/RecipientsList/recoil/state/addRecipient";
import { countryCodesState } from "Pages/RecipientsList/recoil/state/common";

import { withDelay } from "Helpers/delay";
import {
  addRecipient,
  groupsAddUpdater,
  groupsLoaderUpdater,
  groupsRemoveGroupUpdater,
  newRecipientAddGroupsUpdater,
  newRecipientRemoveGroupUpdater,
} from "Pages/RecipientsList/recoil/services/addRecipient";
import { fetchMobilePrefixes } from "Pages/RecipientsList/recoil/services/common";
import {
  addNewGroup,
  fetchGroup,
} from "Pages/RecipientsList/recoil/services/groups";
import { gainsightTrack } from "mailup-react-helpers";

import {
  notifyAddRecipientExisting,
  notifyAddRecipientMultiple,
  notifyAddRecipientSuccess,
} from "Pages/RecipientsList/recoil/services/notifications";

export const panelAddRecipientOpenCallback =
  ({ set, snapshot, reset }: CallbackInterface) =>
  async () => {
    //  reset(panelEditRecipientState)

    const { loaded } = await snapshot.getPromise(countryCodesState);
    if (!loaded) {
      set<PageStateType>(pageState, (prev) => ({
        ...prev,
        loading: true,
      }));

      try {
        const countryCodes = await fetchMobilePrefixes();

        set(countryCodesState, (prev) => ({
          ...prev,
          items: countryCodes,
          loaded: true,
        }));
      } catch (error) {
        logAppError({
          Component: "RecipientsList",
          Method: "panelAddRecipientOpenCallback",
          appError: error,
        });
      }

      set<PageStateType>(pageState, (prev) => ({
        ...prev,
        loading: false,
      }));
    }
    set(panelAddRecipientState, (prev) => ({ ...prev, show: true }));
  };

export const panelAddRecipientCloseCallback =
  ({ reset, set }: CallbackInterface) =>
  async () => {
    gainsightTrack("mup_recipients_click_hideRightPanel", {});
    reset(panelAddRecipientState);
    reset(panelAddRecipientGroupsState);
  };

export const panelAddRecipientChangeFieldCallback =
  ({ set }: CallbackInterface) =>
  async (p: Partial<NewRecipient>) => {
    set(panelAddRecipientState, (prev) => ({
      ...prev,
      recipient: {
        ...prev.recipient,
        ...p,
      },
      isDirty: true,
    }));
  };

export const panelAddRecipientToggleSendConfirmationCallback =
  ({ set }: CallbackInterface) =>
  async () => {
    set(panelAddRecipientState, (prev) => ({
      ...prev,
      sendConfirmation: !prev.sendConfirmation,
    }));
  };

export const panelAddRecipientChangeDynamicFieldCallback =
  ({ set }: CallbackInterface) =>
  async (fieldId: number, value: string) => {
    set(panelAddRecipientState, (prev) => ({
      ...prev,
      recipient: {
        ...prev.recipient,
        Fields: prev.recipient.Fields.filter((f) => f.id !== fieldId).concat({
          id: fieldId,
          value,
        }),
      },
      isDirty: true,
    }));
  };

export const panelAddRecipientAddGroupCallback =
  ({ set }: CallbackInterface) =>
  async (groupId: number | undefined) => {
    if (groupId !== undefined) {
      try {
        set(panelAddRecipientGroupsState, groupsLoaderUpdater(true));

        const group = await fetchGroup(groupId);

        set(panelAddRecipientState, newRecipientAddGroupsUpdater(groupId));

        set(panelAddRecipientGroupsState, groupsAddUpdater(group));

        set(panelAddRecipientGroupsState, groupsLoaderUpdater(false));
      } catch (error) {
        console.error("### error", error);

        logAppError({
          Component: "RecipientsList",
          Method: "panelAddRecipientAddGroupCallback",
          appError: error,
        });

        set(pageState, errorUpdater);
      }
    }
  };

export const panelAddRecipientNewGroupCallback =
  ({ set }: CallbackInterface) =>
  async (groupName: string): Promise<number | undefined> => {
    try {
      set(panelAddRecipientGroupsState, groupsLoaderUpdater(true));

      const newGroup = await addNewGroup(groupName);

      set(panelAddRecipientState, newRecipientAddGroupsUpdater(newGroup.Id));

      set(panelAddRecipientGroupsState, groupsAddUpdater(newGroup));

      set(panelAddRecipientGroupsState, groupsLoaderUpdater(false));

      return newGroup.Id;
    } catch (error) {
      console.error("### error", error);

      logAppError({
        Component: "RecipientsList",
        Method: "panelAddRecipientNewGroupCallback",
        appError: error,
      });
    }
  };

export const panelAddRecipientRemoveGroupCallback =
  ({ snapshot, set }: CallbackInterface) =>
  async (groupId: number) => {
    set(panelAddRecipientGroupsState, groupsLoaderUpdater(true));

    await withDelay(true, 250);

    set(panelAddRecipientState, newRecipientRemoveGroupUpdater(groupId));
    set(panelAddRecipientGroupsState, groupsRemoveGroupUpdater(groupId));

    set(panelAddRecipientGroupsState, groupsLoaderUpdater(false));
  };

export const panelAddRecipientChangePaginationCallback =
  ({ set, snapshot }: CallbackInterface) =>
  async ({
    pageNumber,
    pageSize,
    orderBy,
  }: {
    pageNumber?: number;
    pageSize?: number;
    orderBy?: "Id" | "Name";
  }) => {
    set(panelAddRecipientGroupsState, groupsLoaderUpdater(true));

    const prevPagination = await snapshot.getPromise(
      panelAddRecipientGroupsState,
    );
    const newPagination = {
      pageNumber:
        pageNumber !== undefined ? pageNumber : prevPagination.pageNumber,
      pageSize: pageSize !== undefined ? pageSize : prevPagination.pageSize,
      orderBy: orderBy !== undefined ? orderBy : prevPagination.orderBy,
      orderDescending:
        orderBy !== undefined && orderBy === prevPagination.orderBy
          ? !prevPagination.orderDescending
          : prevPagination.orderDescending,
    };

    await withDelay(true, 250);

    set(panelAddRecipientGroupsState, (prev) => ({
      ...prev,
      ...newPagination,
    }));

    set(panelAddRecipientGroupsState, groupsLoaderUpdater(false));
  };

export const panelAddRecipientSaveCallback =
  ({ set, snapshot }: CallbackInterface) =>
  async () => {
    set<PageStateType>(pageState, (prev) => ({
      ...prev,
      loading: true,
    }));

    const { recipient, sendConfirmation } = await snapshot.getPromise(
      panelAddRecipientState,
    );

    try {
      const { ResultCode, existingRecipientId } = await addRecipient(
        recipient,
        sendConfirmation,
      );

      if (ResultCode === "OK") {
        notifyAddRecipientSuccess();
      }
      if (ResultCode === "EXISTING" && existingRecipientId !== undefined) {
        notifyAddRecipientExisting(existingRecipientId);
      }

      if (ResultCode === "MULTIPLE") {
        notifyAddRecipientMultiple();
      }
    } catch (error) {
      console.error("### error", error);

      logAppError({
        Component: "RecipientsList",
        Method: "panelAddRecipientSaveCallback",
        appError: error,
      });

      set(pageState, errorUpdater);
    }

    set<PageStateType>(pageState, (prev) => ({
      ...prev,
      loading: false,
    }));
  };
